import React, { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Link2, List, ListOrdered, RemoveFormatting, Underline } from 'lucide-react';
import { htmlSeguro } from '../../../lib/formularios';

/*
 * Descripción con formato (negrita, cursiva, subrayado, enlace y listas), como la de Google Forms.
 *
 * Es un contentEditable NO controlado: si se le reescribiera el HTML en cada tecla, el cursor saltaría
 * al principio. Solo se repinta desde fuera cuando no tiene el foco (deshacer, otro editor). Lo que se
 * manda es el HTML tal cual; el servidor lo sanea y al mostrarlo se vuelve a sanear con `htmlSeguro`.
 * `execCommand` está en desuso pero es lo único que da estas órdenes sin un editor completo (el
 * redactor del correo ya lo usa).
 */
export default function TextoEnriquecido({ valor, onCambio, placeholder, deshabilitado, className = '', etiqueta }) {
  const ref = useRef(null);
  const [enfocado, setEnfocado] = useState(false);
  const [pidiendoEnlace, setPidiendoEnlace] = useState(false);
  const [enlace, setEnlace] = useState('');
  const seleccion = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const limpio = htmlSeguro(valor || '');
    if (el.innerHTML !== limpio) el.innerHTML = limpio;
  }, [valor]);

  const orden = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onCambio?.(ref.current?.innerHTML || '');
  };

  const guardarSeleccion = () => {
    const sel = window.getSelection();
    seleccion.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
  };

  const insertarEnlace = (e) => {
    e.preventDefault();
    let url = enlace.trim();
    setPidiendoEnlace(false);
    setEnlace('');
    if (!url) return;
    if (!/^(https?:|mailto:)/i.test(url)) url = `https://${url}`;
    ref.current?.focus();
    const sel = window.getSelection();
    if (seleccion.current && sel) {
      sel.removeAllRanges();
      sel.addRange(seleccion.current);
    }
    if (sel && sel.isCollapsed) document.execCommand('insertHTML', false, `<a href="${url.replace(/"/g, '&quot;')}">${url}</a>`);
    else document.execCommand('createLink', false, url);
    onCambio?.(ref.current?.innerHTML || '');
  };

  const vacio = !textoVisible(valor);

  return (
    <div className={`fe-rico ${enfocado ? 'is-enfocado' : ''} ${className}`}>
      <div
        ref={ref}
        className="fe-rico-area fm-descripcion"
        contentEditable={!deshabilitado}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={etiqueta || placeholder}
        data-placeholder={vacio ? placeholder : ''}
        onFocus={() => setEnfocado(true)}
        onBlur={(e) => {
          // Si el foco pasa a la barra de formato, sigue "enfocado" para que la barra no desaparezca
          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) setEnfocado(false);
        }}
        onInput={(e) => onCambio?.(e.currentTarget.innerHTML)}
        onPaste={(e) => {
          // Pegar como texto: lo que viene de Word o de una web trae estilos que no se pueden guardar
          e.preventDefault();
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }}
      />
      {enfocado && !deshabilitado && (
        <div className="fe-rico-barra" onMouseDown={(e) => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}>
          <button type="button" onClick={() => orden('bold')} aria-label="Negrita" title="Negrita"><Bold /></button>
          <button type="button" onClick={() => orden('italic')} aria-label="Cursiva" title="Cursiva"><Italic /></button>
          <button type="button" onClick={() => orden('underline')} aria-label="Subrayado" title="Subrayado"><Underline /></button>
          <button
            type="button"
            onClick={() => { guardarSeleccion(); setPidiendoEnlace((v) => !v); }}
            aria-label="Insertar enlace"
            title="Insertar enlace"
          >
            <Link2 />
          </button>
          <button type="button" onClick={() => orden('insertUnorderedList')} aria-label="Lista con viñetas" title="Lista con viñetas"><List /></button>
          <button type="button" onClick={() => orden('insertOrderedList')} aria-label="Lista numerada" title="Lista numerada"><ListOrdered /></button>
          <button type="button" onClick={() => { orden('removeFormat'); orden('unlink'); }} aria-label="Borrar formato" title="Borrar formato"><RemoveFormatting /></button>
          {pidiendoEnlace && (
            <form className="fe-rico-enlace" onSubmit={insertarEnlace}>
              <input
                autoFocus
                value={enlace}
                onChange={(e) => setEnlace(e.target.value)}
                placeholder="https://…"
                aria-label="Dirección del enlace"
                onBlur={() => setEnfocado(true)}
              />
              <button type="submit" className="fe-btn-texto">Aplicar</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function textoVisible(html) {
  return !!String(html || '').replace(/<br\s*\/?>/gi, '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}
